// Push Notification Utilities for Insite360
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to make authenticated API calls
const getApi = () => {
  const token = localStorage.getItem('token');
  return axios.create({
    baseURL: `${API_URL}/api`,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
};

// Convert VAPID key from base64 to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Check if push notifications are supported
export function isPushSupported() {
  return 'serviceWorker' in navigator && 
         'PushManager' in window && 
         'Notification' in window;
}

// Get current notification permission status
export function getNotificationPermission() {
  if (!('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission; // 'granted', 'denied', or 'default'
}

// Request notification permission
export async function requestNotificationPermission() {
  if (!('Notification' in window)) {
    throw new Error('Notifications not supported');
  }
  
  const permission = await Notification.requestPermission();
  return permission;
}

// Register service worker
export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service workers not supported');
  }
  
  try {
    const registration = await navigator.serviceWorker.register('/sw.js');
    console.log('Service worker registered:', registration);
    return registration;
  } catch (error) {
    console.error('Service worker registration failed:', error);
    throw error;
  }
}

// Get existing service worker registration
export async function getServiceWorkerRegistration() {
  if (!('serviceWorker' in navigator)) {
    return null;
  }
  return await navigator.serviceWorker.ready;
}

// Subscribe to push notifications
export async function subscribeToPush() {
  try {
    // 1. Check support
    if (!isPushSupported()) {
      throw new Error('Push notifications not supported in this browser');
    }
    
    // 2. Request permission
    const permission = await requestNotificationPermission();
    if (permission !== 'granted') {
      throw new Error('Notification permission denied');
    }
    
    // 3. Register service worker
    const registration = await registerServiceWorker();
    
    // 4. Get VAPID public key from server
    const { data: vapidData } = await getApi().get('/push/vapid-public-key');
    const vapidPublicKey = vapidData.publicKey;
    
    if (!vapidPublicKey) {
      throw new Error('Push notifications not configured on server');
    }
    
    // 5. Subscribe to push
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
    });
    
    // 6. Send subscription to server
    const subscriptionJson = subscription.toJSON();
    await getApi().post('/push/subscribe', {
      endpoint: subscriptionJson.endpoint,
      keys: subscriptionJson.keys
    });
    
    console.log('Push subscription successful');
    return { success: true, subscription };
    
  } catch (error) {
    console.error('Push subscription error:', error);
    throw error;
  }
}

// Unsubscribe from push notifications
export async function unsubscribeFromPush() {
  try {
    const registration = await getServiceWorkerRegistration();
    if (!registration) {
      return { success: true, message: 'No service worker' };
    }
    
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      return { success: true, message: 'No subscription' };
    }
    
    // Unsubscribe locally
    await subscription.unsubscribe();
    
    // Notify server
    await getApi().delete('/push/unsubscribe', {
      params: { endpoint: subscription.endpoint }
    });
    
    console.log('Push unsubscription successful');
    return { success: true };
    
  } catch (error) {
    console.error('Push unsubscription error:', error);
    throw error;
  }
}

// Get current push subscription status
export async function getPushStatus() {
  try {
    // Check local status
    const localStatus = {
      supported: isPushSupported(),
      permission: getNotificationPermission(),
      subscribed: false
    };
    
    if (!localStatus.supported) {
      return localStatus;
    }
    
    // Check if we have an active subscription
    const registration = await getServiceWorkerRegistration();
    if (registration) {
      const subscription = await registration.pushManager.getSubscription();
      localStatus.subscribed = !!subscription;
    }
    
    // Get server status
    try {
      const { data } = await getApi().get('/push/status');
      return {
        ...localStatus,
        serverEnabled: data.enabled,
        subscriptionCount: data.subscription_count,
        vapidConfigured: data.vapid_configured
      };
    } catch (e) {
      return localStatus;
    }
    
  } catch (error) {
    console.error('Error getting push status:', error);
    return {
      supported: isPushSupported(),
      permission: getNotificationPermission(),
      subscribed: false,
      error: error.message
    };
  }
}

// Send test notification
export async function sendTestNotification() {
  try {
    const { data } = await getApi().post('/push/test');
    return data;
  } catch (error) {
    console.error('Test notification error:', error);
    throw error;
  }
}

// Show local notification (for testing without server)
export function showLocalNotification(title, options = {}) {
  if (Notification.permission !== 'granted') {
    console.warn('Notification permission not granted');
    return;
  }
  
  return new Notification(title, {
    icon: '/insite360_logo.png',
    badge: '/insite360_logo.png',
    ...options
  });
}
